import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatPrice, FALLBACK_IMG } from "@/lib/api";
import { EmptyState } from "@/components/States";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";

export default function Cart() {
  const { items, updateQuantity, removeItem, subtotal } = useCart();

  if (items.length === 0)
    return (
      <div className="mx-auto max-w-4xl px-4 py-16">
        <EmptyState
          title="Your cart is empty"
          subtitle="Looks like you haven't added anything yet."
          action={
            <Link to="/shop" data-testid="cart-empty-shop-btn" className="rounded-full bg-brand px-6 py-3 font-medium text-white">
              Browse Products
            </Link>
          }
        />
      </div>
    );

  return (
    <div className="mx-auto max-w-6xl animate-fade-in px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="mb-8 font-heading text-4xl text-brand-dark">Your Cart</h1>
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {items.map((item, i) => (
            <div
              key={i}
              data-testid={`cart-item-${i}`}
              className="flex gap-4 rounded-2xl border border-brand-line bg-white p-4"
            >
              <img src={item.image || FALLBACK_IMG} alt={item.name} className="h-24 w-24 flex-shrink-0 rounded-xl object-cover" />
              <div className="flex flex-1 flex-col">
                <div className="flex justify-between">
                  <div>
                    <h3 className="font-heading text-xl text-brand-dark">{item.name}</h3>
                    <p className="text-sm text-brand-muted">{item.variant_label}</p>
                    {item.customization && (
                      <p className="mt-1 text-sm italic text-brand">
                        {item.customization_label ? `${item.customization_label}: ` : ""}“{item.customization}”
                      </p>
                    )}
                  </div>
                  <button
                    data-testid={`cart-remove-${i}`}
                    onClick={() => removeItem(i)}
                    className="h-fit rounded-full p-2 text-brand-muted transition-colors hover:bg-red-50 hover:text-red-500"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-auto flex items-center justify-between pt-3">
                  <div className="inline-flex items-center rounded-full border border-brand-line">
                    <button data-testid={`cart-dec-${i}`} onClick={() => updateQuantity(i, item.quantity - 1)} className="p-2 text-brand-dark">
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center font-semibold">{item.quantity}</span>
                    <button data-testid={`cart-inc-${i}`} onClick={() => updateQuantity(i, Math.min(item.stock ?? Infinity, item.quantity + 1))} disabled={item.stock != null && item.quantity >= item.stock} className="p-2 text-brand-dark disabled:opacity-40">
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="font-semibold text-brand">{formatPrice(item.price * item.quantity)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="h-fit rounded-2xl border border-brand-line bg-white p-6">
          <h2 className="font-heading text-2xl text-brand-dark">Order Summary</h2>
          <div className="mt-4 space-y-3 border-b border-brand-line pb-4 text-sm">
            <div className="flex justify-between text-brand-muted">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between text-brand-muted">
              <span>Delivery</span>
              <span>Calculated at order</span>
            </div>
          </div>
          <div className="flex justify-between py-4 text-lg font-semibold text-brand-dark">
            <span>Total</span>
            <span data-testid="cart-total">{formatPrice(subtotal)}</span>
          </div>
          <Link
            to="/checkout"
            data-testid="checkout-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 font-medium text-white transition-transform hover:-translate-y-0.5 hover:bg-brand-hover"
          >
            Proceed to Checkout <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/shop" className="mt-3 block text-center text-sm text-brand-muted hover:text-brand">
            Continue shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
